/**
 * 极简的模块级外部 store，配合 React 的 useSyncExternalStore 使用。
 *
 * 用途：同一份链上数据常被多个组件同时使用（例如全局统计被 3 个组件引用、
 * 猫详情被 4 个面板引用）。若每个组件各自 useState + 各自请求，
 * 既是成倍的 RPC 消耗，一个组件的刷新也无法同步到其他组件。
 * 统一收敛到模块级 store 后，全局只保留一份数据和一次请求。
 */

export interface Store<T> {
  get(): T;
  set(next: T): void;
  subscribe(listener: () => void): () => void;
}

export function createStore<T>(initial: T): Store<T> {
  let snapshot = initial;
  const listeners = new Set<() => void>();

  return {
    // useSyncExternalStore 要求同一状态返回同一引用，因此整体替换而非就地修改
    get: () => snapshot,
    set(next: T) {
      snapshot = next;
      listeners.forEach((l) => l());
    },
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/** 按 key 分组的 store 集合（例如按 tokenId 缓存猫详情） */
export function createKeyedStore<T>(initial: T) {
  const stores = new Map<string, Store<T>>();
  return (key: string): Store<T> => {
    let store = stores.get(key);
    if (!store) {
      store = createStore(initial);
      stores.set(key, store);
    }
    return store;
  };
}
